import { useState } from "react";
import { paths } from "@/routes/paths";
import { Link } from "react-router-dom";
import { useRouter } from "@/routes/hooks";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuthContext } from "@/hooks/use-auth-context";
import { createCheckoutSession } from "@/api/subscription";
import { useWorkspaceContext } from "@/hooks/use-workspace-context";
import { ElementShowcase } from "@/components/ui-custom/ElementShowcase";
import {
  PricingCard,
  type PricingTier,
} from "@/components/pricing/PricingCard";
import {
  TestimonialCard,
  type Testimonial,
} from "@/components/testimonials/TestimonialCard";
import {
  Carousel,
  CarouselItem,
  CarouselNext,
  CarouselContent,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Zap,
  Mic,
  Clock,
  Users,
  QrCode,
  FileText,
  PieChart,
  FileCheck,
  LineChart,
  Languages,
  ArrowRight,
  FolderTree,
  CheckCircle,
  ShieldCheck,
  FileSignature,
} from "lucide-react";

const Index = () => {
  const router = useRouter();
  const [isYearly, setIsYearly] = useState(false);
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const { workspace } = useWorkspaceContext();
  const { authenticated } = useAuthContext();
  const { toast } = useToast();

  const handleStartFreeTrial = async (tier: PricingTier, isYearly: boolean) => {
    if (!authenticated) {
      toast({
        title: "Authentication required",
        description: "Please log in or sign up to start a free trial",
        variant: "destructive",
      });
      return;
    }

    if (!workspace) {
      toast({
        title: "Workspace required",
        description: "Please create a workspace first",
        variant: "destructive",
      });
      return;
    }

    setLoadingTier(tier.name);

    try {
      // Create checkout session
      const response = await createCheckoutSession({
        plan: tier.name.toLowerCase(),
        frequency: isYearly ? "yearly" : "monthly",
        workspaceId: workspace.id,
        trialPeriodDays: tier.freeTrialDays || 14,
      });

      // Redirect to Stripe checkout
      if (response?.url) {
        window.location.href = response.url;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Error starting free trial:", error);
      toast({
        title: "Error",
        description: "Failed to start free trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <AppLayout>
      {/* Hero Section - AI-focused compelling design */}
      <section className="relative w-full overflow-hidden">
        {/* Enhanced gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background/90 to-background z-0" />

        {/* Enhanced decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse z-0 opacity-70"></div>
        <div className="absolute bottom-40 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl z-0 opacity-70"></div>
        <div className="absolute top-60 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl z-0 opacity-60"></div>

        {/* Full width content container */}
        <div className="w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 mx-auto py-16 md:py-28 relative z-10">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-6 lg:col-span-5 animate-fade-in">
              <div className="flex items-center mb-6">
                <div className="bg-primary/10 text-primary text-sm font-medium py-1 px-3 rounded-full mr-3">
                  Revolutionary
                </div>
                <span className="text-sm font-medium text-muted-foreground">
                  90% Time Savings, Guaranteed
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                <span className="text-primary bg-clip-text bg-gradient-to-r from-primary to-blue-500">
                  Multilingual Forms
                </span>{" "}
                With Speech-to-Text Powers
              </h1>

              <p className="text-xl text-muted-foreground mb-6 leading-relaxed max-w-xl">
                Create forms that speak your users' language. Auto-translate
                into 10+ languages and let users respond by voice. Boost
                accessibility and conversion rates with next-gen forms.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-5 w-5 text-primary mr-2" />
                  <span>20+ Smart Field Types</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-5 w-5 text-primary mr-2" />
                  <span>Voice-to-Form Technology</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-5 w-5 text-primary mr-2" />
                  <span>98% Completion Rates</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-5 w-5 text-primary mr-2" />
                  <span>Multilingual Support</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-5 w-5 text-primary mr-2" />
                  <span>Speech-to-Text Responses</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => router.push(paths.pricing)}
                  size="lg"
                  className="group bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-700"
                >
                  Start Your 14-Day Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => router.push(paths.formDemo)}
                >
                  Watch Demo
                </Button>
              </div>

              <p className="mt-2 text-sm text-primary font-medium">
                No credit card required to start your free trial
              </p>

              <div className="mt-6 flex items-center">
                <div className="flex -space-x-2">
                  <div className="h-8 w-8 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <div className="h-8 w-8 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                    <PieChart className="h-4 w-4 text-primary" />
                  </div>
                  <div className="h-8 w-8 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                  </div>
                  <div className="h-8 w-8 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                </div>
                <p className="ml-4 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">2,500+</span>{" "}
                  businesses getting more done
                </p>
              </div>
            </div>

            <div className="md:col-span-6 lg:col-span-7 max-w-3xl justify-self-end animate-slide-up">
              <div className="relative">
                <div className="absolute -top-4 -left-4 right-0 bottom-0 rounded-2xl bg-gradient-to-br from-primary/30 to-blue-600/30 blur-lg opacity-50"></div>

                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-card border border-border">
                  <div className="p-2 bg-card border-b border-border flex items-center">
                    <div className="flex gap-1.5 ml-1">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                  </div>

                  <div className="relative">
                    <img
                      src="https://cdn.formcraft.cloud/app/dashboard_dark.png"
                      alt="FormCraft Dashboard"
                      className="w-full object-cover dark:block hidden"
                    />
                    <img
                      src="https://cdn.formcraft.cloud/app/dashboard_light.png"
                      alt="FormCraft Dashboard"
                      className="w-full object-cover dark:hidden block"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Form Builder Highlight Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-primary/20 bg-card">
                <div className="p-4 bg-primary/5 border-b border-primary/20">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-bold">AI</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Your Form Assistant</h4>
                      <div className="text-xs text-muted-foreground">
                        Type or speak your request
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4 bg-background/50">
                  <div className="bg-muted/50 p-3 rounded-lg max-w-[80%]">
                    <p className="text-sm">
                      I need a customer feedback form with satisfaction rating,
                      product selection, and open comments.
                    </p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-lg max-w-[80%] ml-auto">
                    <p className="text-sm">
                      Creating your feedback form with NPS rating, product
                      dropdown, and comment field. Would you like to add contact
                      information collection?
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg max-w-[80%]">
                    <p className="text-sm">
                      Yes, make it optional and add a "Contact me about my
                      feedback" checkbox.
                    </p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-lg max-w-[80%] ml-auto">
                    <p className="text-sm">
                      Form created! I've added conditional logic so contact
                      fields only appear when the checkbox is selected. Ready to
                      customize or publish.
                    </p>
                  </div>
                  <div className="mt-6 text-center">
                    <Button variant="outline" size="sm">
                      View Generated Form
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="flex items-center gap-2 mb-6">
                <div className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                  Breakthrough AI
                </div>
                <div className="h-px bg-primary/20 flex-grow"></div>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Simply Ask, and Your Perfect Form Appears
              </h2>

              <p className="text-lg text-muted-foreground mb-8">
                Just describe what you need—in your own words—and our AI
                instantly builds sophisticated forms with intelligent
                validation, conditional logic, and user-friendly design.
              </p>

              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-1">
                    <span className="text-primary font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-lg mb-1">
                      Speak or Type Your Needs
                    </h4>
                    <p className="text-muted-foreground">
                      Just tell our AI what you need—use natural language,
                      record audio, or choose from smart suggestions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-1">
                    <span className="text-primary font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-lg mb-1">
                      Watch AI Build in Real-Time
                    </h4>
                    <p className="text-muted-foreground">
                      See your form materialize in seconds with the perfect
                      fields, validation rules, and conditional logic already in
                      place.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-1">
                    <span className="text-primary font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-lg mb-1">
                      Publish & Start Collecting
                    </h4>
                    <p className="text-muted-foreground">
                      Make quick adjustments if needed, then share your form and
                      watch responses flow in with 98% completion rates.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                size="lg"
                className="group"
                onClick={() => router.push(paths.auth.signup)}
              >
                Create Your First Form Free
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full h-px bg-border/60 max-w-7xl mx-auto"></div>

      {/* Form Translation Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-background relative overflow-hidden">
        {/* Enhanced decorative elements */}
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl z-0 opacity-30"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl z-0 opacity-40"></div>

        <div className="container px-4 sm:px-6 lg:px-8 mx-auto relative z-10">
          <div className="text-center mb-10">
            <div className="px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full inline-flex items-center gap-2 mb-3">
              <Languages className="h-4 w-4" />
              <span>Multilingual Experience</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-5 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-600">
              Automatic Form Translation
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
              Break language barriers with instant form translations. Your forms
              automatically appear in your users' preferred language.
            </p>

            <Button
              onClick={() => router.push(paths.formDemo)}
              variant="outline"
              size="lg"
              className="group"
            >
              See Translation in Action
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          <div className="max-w-5xl mx-auto mt-10">
            <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-stretch">
              <div className="relative">
                {/* Subtle highlight effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-2xl blur-lg opacity-40"></div>

                <div className="rounded-2xl overflow-hidden border border-primary/20 shadow-lg h-full bg-card relative">
                  <div className="p-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-primary/15 rounded-full p-2">
                        <span className="text-primary font-bold text-xs">
                          EN
                        </span>
                      </div>
                      <h4 className="font-medium">English Original</h4>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    </div>
                  </div>
                  <div className="p-5 bg-card/80">
                    <div className="p-5 bg-background border border-border rounded-lg shadow-sm mb-4">
                      <h3 className="font-medium mb-3 pb-2 border-b border-border/70">
                        Patient Information Form
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Full Name
                          </label>
                          <div className="h-10 w-full bg-muted/50 rounded-md border border-border/80"></div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Date of Birth
                          </label>
                          <div className="h-10 w-full bg-muted/50 rounded-md border border-border/80"></div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Do you have any allergies?
                          </label>
                          <div className="space-y-2 mt-1">
                            <div className="flex items-center gap-2">
                              <div className="h-4 w-4 rounded-full border border-primary flex-shrink-0 bg-primary/5"></div>
                              <span className="text-sm">Yes</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="h-4 w-4 rounded-full border border-border flex-shrink-0"></div>
                              <span className="text-sm">No</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            If yes, please list your allergies:
                          </label>
                          <div className="h-24 w-full bg-muted/50 rounded-md border border-border/80"></div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground flex items-center justify-center">
                        <svg
                          className="h-4 w-4 mr-1 text-primary"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                        English is the default language for all forms
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                {/* Subtle highlight effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-primary/20 rounded-2xl blur-lg opacity-40"></div>

                <div className="rounded-2xl overflow-hidden border border-primary/20 shadow-lg h-full bg-card relative">
                  <div className="p-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-primary/15 rounded-full p-2">
                        <span className="text-primary font-bold text-xs">
                          DE
                        </span>
                      </div>
                      <h4 className="font-medium">German Translation</h4>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    </div>
                  </div>
                  <div className="p-5 bg-card/80">
                    <div className="p-5 bg-background border border-border rounded-lg shadow-sm mb-4">
                      <h3 className="font-medium mb-3 pb-2 border-b border-border/70">
                        Patienteninformationsformular
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Vollständiger Name
                          </label>
                          <div className="h-10 w-full bg-muted/50 rounded-md border border-border/80"></div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Geburtsdatum
                          </label>
                          <div className="h-10 w-full bg-muted/50 rounded-md border border-border/80"></div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Haben Sie Allergien?
                          </label>
                          <div className="space-y-2 mt-1">
                            <div className="flex items-center gap-2">
                              <div className="h-4 w-4 rounded-full border border-primary flex-shrink-0 bg-primary/5"></div>
                              <span className="text-sm">Ja</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="h-4 w-4 rounded-full border border-border flex-shrink-0"></div>
                              <span className="text-sm">Nein</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1.5">
                            Wenn ja, bitte listen Sie Ihre Allergien auf:
                          </label>
                          <div className="h-24 w-full bg-muted/50 rounded-md border border-border/80"></div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground flex items-center justify-center">
                        <svg
                          className="h-4 w-4 mr-1 text-primary"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                        Forms automatically translate to the user's language
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center border-t border-border/50 pt-10">
              <p className="text-sm text-muted-foreground mb-4">
                Available in 10+ languages:
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  English
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  German
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Spanish
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  French
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Italian
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Portuguese
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Japanese
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Chinese
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Russian
                </span>
                <span className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  Arabic
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full h-px bg-border/60 max-w-7xl mx-auto"></div>

      {/* Form Display Styles Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="text-center mb-16">
            <div className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full inline-block mb-2">
              Flexible Display Options
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Choose How Your Forms Appear
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Create the perfect user experience with multiple display styles -
              conversational for engagement or standard for familiarity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="rounded-2xl overflow-hidden border border-primary/10 shadow-lg">
                <div className="p-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary/10 rounded-full p-1.5">
                      <span className="text-primary font-medium text-xs">
                        1/5
                      </span>
                    </div>
                    <h4 className="font-medium text-sm">
                      Conversational Style
                    </h4>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                </div>
                <div className="relative p-4">
                  <img
                    src="/assets/home/conversational_display_dark.png"
                    alt="Conversational Form Style"
                    className="w-full rounded-lg shadow-md dark:block hidden"
                  />
                  <img
                    src="/assets/home/conversational_display_light.png"
                    alt="Conversational Form Style"
                    className="w-full rounded-lg shadow-md dark:hidden block"
                  />
                  <div className="mt-4 text-center">
                    <h3 className="font-medium text-lg">
                      Typeform-Style Conversation
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Present one question at a time for higher engagement and
                      completion rates. Perfect for surveys and feedback forms.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="rounded-2xl overflow-hidden border border-primary/10 shadow-lg">
                <div className="p-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary/10 rounded-full p-1.5">
                      <span className="text-primary font-medium text-xs">
                        2/5
                      </span>
                    </div>
                    <h4 className="font-medium text-sm">Standard Style</h4>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                </div>
                <div className="relative p-4">
                  <img
                    src="/assets/home/standard_display_dark.png"
                    alt="Standard Form Style"
                    className="w-full rounded-lg shadow-md dark:block hidden"
                  />
                  <img
                    src="/assets/home/standard_display_light.png"
                    alt="Standard Form Style"
                    className="w-full rounded-lg shadow-md dark:hidden block"
                  />
                  <div className="mt-4 text-center">
                    <h3 className="font-medium text-lg">
                      Google Forms-Style Standard
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Show all questions at once for faster completion. Ideal
                      for longer forms where users need to see all questions
                      upfront.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button
              onClick={() => router.push(paths.formDemo)}
              variant="outline"
              size="lg"
            >
              See Forms in Action
            </Button>
          </div>
        </div>
      </section>

      {/* Form Elements Showcase Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-background via-primary/5 to-background relative overflow-hidden">
        <div className="absolute top-40 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl z-0 opacity-30"></div>
        <div className="absolute bottom-40 right-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl z-0 opacity-40"></div>

        <div className="container px-4 sm:px-6 lg:px-8 mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full inline-block mb-2">
              Interactive Form Components
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              25+ Powerful Form Elements
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our comprehensive collection of form elements - from
              simple text inputs to advanced components like payment processing,
              QR codes, and digital signatures.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <ElementShowcase />

            <div className="mt-10 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Every element is fully customizable with conditional logic,
                validation rules, and styling options
              </p>
              <Button
                onClick={() => router.push(paths.formDemo)}
                size="lg"
                className="group"
              >
                Try All Elements
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Voice Features Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="text-center mb-16">
            <div className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full inline-block mb-2">
              Voice-Powered Experience
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Say Goodbye to Typing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Use voice for both form creation and form responses - saving time
              and increasing accessibility.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Form Creation with Voice */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg">
              <div className="p-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/10 rounded-full p-1.5">
                    <span className="text-primary font-medium text-xs">1</span>
                  </div>
                  <h4 className="font-medium text-sm">
                    Voice-to-Form Technology
                  </h4>
                </div>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                </div>
              </div>

              <div className="p-6">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-6 mb-4">
                  <div className="flex flex-col items-center justify-center py-4">
                    <div className="relative mb-4">
                      <div className="size-20 rounded-full bg-indigo-500/20 animate-pulse flex items-center justify-center">
                        <div className="size-16 rounded-full bg-indigo-500 text-white flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-mic"
                          >
                            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                            <path d="M12 19v3" />
                          </svg>
                        </div>
                      </div>
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-2 py-1 font-medium">
                        00:18
                      </div>
                    </div>
                    <h3 className="text-lg font-medium mb-2">
                      Recording in progress...
                    </h3>
                    <p className="text-muted-foreground text-center max-w-md text-sm">
                      "I need a patient intake form with personal details,
                      medical history, current medications, and insurance
                      information..."
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium">Form Generated!</h3>
                    <div className="flex items-center gap-1 text-green-500 text-xs font-medium">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-check"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span>Done in 3 seconds</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 mt-3">
                    <div className="col-span-4 p-2 bg-muted rounded border border-border">
                      <div className="h-4 w-3/4 bg-primary/10 rounded"></div>
                    </div>
                    <div className="col-span-2 p-2 bg-muted rounded border border-border">
                      <div className="h-4 w-1/2 bg-primary/10 rounded"></div>
                    </div>
                    <div className="col-span-2 p-2 bg-muted rounded border border-border">
                      <div className="h-4 w-1/2 bg-primary/10 rounded"></div>
                    </div>
                    <div className="col-span-4 p-2 bg-muted rounded border border-border">
                      <div className="h-4 w-2/3 bg-primary/10 rounded"></div>
                    </div>
                    <div className="col-span-4 p-2 bg-muted rounded border border-border">
                      <div className="h-4 w-1/4 bg-primary/10 rounded"></div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="font-medium text-lg">
                    Create Forms with Your Voice
                  </h3>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Just describe what you need, and our AI instantly builds
                    your form—complete with the right fields, validation, and
                    logic. No typing, no design skills needed.
                  </p>
                </div>
              </div>
            </div>

            {/* Voice Response Feature */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg">
              <div className="p-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/10 rounded-full p-1.5">
                    <span className="text-primary font-medium text-xs">2</span>
                  </div>
                  <h4 className="font-medium text-sm">
                    Voice Response Feature
                  </h4>
                </div>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                </div>
              </div>

              <div className="p-6">
                <div className="bg-card shadow-xl rounded-lg border border-border w-72 overflow-hidden mx-auto mb-4">
                  <div className="p-3 flex justify-between items-center border-b">
                    <h3 className="font-medium text-sm">Voice Response</h3>
                    <button className="h-6 w-6 p-0 flex items-center justify-center rounded-full hover:bg-muted">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-x"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="p-4 flex flex-col items-center">
                    <div className="font-mono text-lg font-medium mb-3">
                      00:42
                    </div>

                    <div className="mb-3">
                      <button className="h-12 w-12 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-square"
                        >
                          <rect width="18" height="18" x="3" y="3" rx="2" />
                        </svg>
                      </button>
                    </div>

                    <p className="text-xs text-muted-foreground text-center mb-2">
                      Speak clearly and answer all form questions
                    </p>

                    <div className="w-full bg-slate-100 dark:bg-slate-800 h-1 mb-3 rounded-full overflow-hidden">
                      <div className="bg-primary h-full w-3/4 animate-pulse"></div>
                    </div>

                    <div className="w-full text-center text-xs text-muted-foreground">
                      <span>Currently answering:</span>
                      <p className="font-medium text-foreground mt-1">
                        Please describe your symptoms
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="font-medium text-lg">
                    Fill Forms with Voice Responses
                  </h3>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Let your form respondents speak their answers instead of
                    typing. Perfect for mobile users, those with accessibility
                    needs, or when detailed responses are required.
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-check text-primary mt-0.5"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span className="text-sm">
                        Boosts accessibility and engagement
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-check text-primary mt-0.5"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span className="text-sm">
                        Automatically transcribes responses
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-check text-primary mt-0.5"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span className="text-sm">
                        Increases completion rates by 22%
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need, None of the Complexity
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features that drive engagement, boost conversions, and
              eliminate the frustration of traditional form builders.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-primary/10 rounded-full p-3 inline-block mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Businesses Getting Results
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how teams achieve 90% time savings and 98% form completion
              rates.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="mx-auto"
            >
              <CarouselContent>
                {detailedTestimonials.map((testimonial, index) => (
                  <CarouselItem
                    key={index}
                    className="md:basis-1/2 lg:basis-1/3 pl-4 sm:pl-6"
                  >
                    <div className="p-1">
                      <TestimonialCard testimonial={testimonial} />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-center mt-8">
                <CarouselPrevious className="relative mr-2" />
                <CarouselNext className="relative ml-2" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Scale As You Grow
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose a plan that fits your needs. All plans include a 14-day
              free trial, unlimited forms, and access to all form elements.
            </p>

            <div className="flex items-center justify-center mt-8">
              <div className="bg-muted inline-flex p-1 rounded-full">
                <button
                  className={`px-4 py-2 rounded-full ${
                    !isYearly
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground"
                  }`}
                  onClick={() => setIsYearly(false)}
                >
                  Monthly
                </button>
                <button
                  className={`px-4 py-2 rounded-full ${
                    isYearly
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground"
                  }`}
                  onClick={() => setIsYearly(true)}
                >
                  Annual
                </button>
              </div>
              <span className="ml-2 text-sm text-primary font-medium">
                Save 17%
              </span>
            </div>
            <p className="text-sm text-primary font-medium mt-2">
              No credit card required to start your free trial
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto relative">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`${
                  tier.popular ? "pt-4" : ""
                } relative animate-fade-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <PricingCard
                  tier={tier}
                  isYearly={isYearly}
                  onStartFreeTrial={handleStartFreeTrial}
                  loading={loadingTier === tier.name}
                />
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-muted-foreground mb-4">
              Need enterprise features or high-volume solution?
            </p>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Let's Talk</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/5">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1">
              <div className="mb-6">
                <div className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full inline-block mb-2">
                  Business Impact
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">
                  Stop Wasting Time & Money
                </h2>
              </div>
              <p className="text-lg text-muted-foreground mb-8">
                Our AI form builder eliminates frustrating form creation delays,
                design work, and development costs.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 rounded-full p-2">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-xl font-semibold">90% Time Saved</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 rounded-full p-2">
                    <LineChart className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-xl font-semibold">
                    98% Completion Rate
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 rounded-full p-2">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-xl font-semibold">
                    Zero Dev Resources
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-lg">
                <h3 className="text-xl font-semibold mb-6">
                  Form Creation ROI Calculator
                </h3>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-background rounded-lg p-5 border border-border">
                      <h4 className="text-sm text-muted-foreground mb-2">
                        Traditional Form Builder
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Time to Create
                          </div>
                          <div className="text-2xl font-bold">3-5 Hours</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Completion Rate
                          </div>
                          <div className="text-2xl font-bold">70-80%</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Monthly Cost
                          </div>
                          <div className="text-2xl font-bold">$$$</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Design Skills
                          </div>
                          <div className="text-2xl font-bold">Required</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-primary/5 rounded-lg p-5 border border-primary/20">
                      <h4 className="text-sm text-primary mb-2">
                        AI Form Builder
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Time to Create
                          </div>
                          <div className="text-2xl font-bold text-primary">
                            30 Seconds
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Completion Rate
                          </div>
                          <div className="text-2xl font-bold text-primary">
                            95-98%
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Monthly Cost
                          </div>
                          <div className="text-2xl font-bold text-primary">
                            $
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">
                            Design Skills
                          </div>
                          <div className="text-2xl font-bold text-primary">
                            Not Needed
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-border pt-6">
                    <h4 className="text-xl font-semibold mb-4">
                      Your Business Impact
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-background rounded-lg p-4 border border-border text-center">
                        <div className="text-3xl font-bold text-primary">
                          175+
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Hours Saved Monthly
                        </div>
                      </div>
                      <div className="bg-background rounded-lg p-4 border border-border text-center">
                        <div className="text-3xl font-bold text-primary">
                          32%
                        </div>
                        <div className="text-sm text-muted-foreground">
                          More Conversions
                        </div>
                      </div>
                      <div className="bg-background rounded-lg p-4 border border-border text-center">
                        <div className="text-3xl font-bold text-primary">
                          $7,800
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Annual Savings
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/5">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <div className="max-w-5xl mx-auto bg-card rounded-2xl overflow-hidden shadow-xl border border-primary/20">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12 md:border-r border-primary/10">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-6">
                  <span className="animate-pulse relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  Limited-Time Offer
                </div>

                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Create Your First AI Form in 30 Seconds
                </h2>

                <p className="text-lg text-muted-foreground mb-8">
                  Join 2,500+ businesses saving hours every week. Sign up now
                  and get unlimited access to all premium features for 14 days –
                  absolutely free.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-full p-2 flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">No credit card required</h4>
                      <p className="text-sm text-muted-foreground">
                        Start free, cancel anytime
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-full p-2 flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">AI form creation included</h4>
                      <p className="text-sm text-muted-foreground">
                        Unlimited forms during trial
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 rounded-full p-2 flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">
                        Full access to all features
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Try everything with no limits
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-700"
                    asChild
                  >
                    <Link to="/auth/signup" className="group">
                      Start Free Trial
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/pricing">See All Plans</Link>
                  </Button>
                </div>
              </div>

              <div className="bg-primary/5 p-8 md:p-12 flex flex-col justify-center">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <img
                      src="https://cdn.formcraft.cloud/app/maya-love.png"
                      alt="Dr. Maya Patel"
                      className="w-12 h-12 rounded-full object-cover border-2 border-background"
                    />
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div key={star} className="text-primary">
                            ★
                          </div>
                        ))}
                      </div>
                      <p className="text-sm mb-2">
                        "Our healthcare practice was drowning in paperwork.
                        Patient intake forms took 20+ minutes to complete and
                        our staff spent hours daily on data entry. With
                        FormCraft's voice features and multilingual support,
                        we've cut processing time by 68% and patient
                        satisfaction is at an all-time high."
                      </p>
                      <div>
                        <p className="font-medium">Dr. Maya Patel</p>
                        <p className="text-xs text-muted-foreground">
                          Medical Director, Wellspring Healthcare
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-primary/10 pt-6">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-primary">
                          90%
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Time Saved
                        </p>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary">
                          98%
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Completion Rate
                        </p>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary">
                          30s
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Creation Time
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-primary/10 pt-6">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-muted-foreground">
                        Used by 2,500+ businesses
                      </div>
                      <div className="flex -space-x-2">
                        <div className="h-6 w-6 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                          <FileText className="h-3 w-3 text-primary" />
                        </div>
                        <div className="h-6 w-6 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                          <Mic className="h-3 w-3 text-primary" />
                        </div>
                        <div className="h-6 w-6 rounded-full border-2 border-background bg-primary/10 flex items-center justify-center">
                          <Languages className="h-3 w-3 text-primary" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

const features = [
  {
    title: "AI Form Generator",
    description:
      "Describe or speak your form needs in plain language. Our AI instantly builds professional forms with smart validation and logic.",
    icon: Zap,
  },
  {
    title: "Multilingual Support",
    description:
      "Forms automatically translate into 10+ languages, allowing users to submit responses in their preferred language with one click.",
    icon: Languages,
  },
  {
    title: "Voice-to-Form Technology",
    description:
      "Simply record your form requirements and watch as our AI transcribes your voice and builds your form in real-time.",
    icon: FileText,
  },
  {
    title: "Speech-to-Text Responses",
    description:
      "Let users speak their answers instead of typing. Our advanced speech recognition captures responses accurately in real-time.",
    icon: Mic,
  },
  {
    title: "Drag-and-Drop Editor",
    description:
      "Fine-tune your AI-generated forms with an intuitive drag-and-drop interface that anyone can master in minutes.",
    icon: FileCheck,
  },
  {
    title: "20+ Smart Field Types",
    description:
      "From digital signatures to file uploads, NPS ratings to conditional questions – every field type you'll ever need.",
    icon: CheckCircle,
  },
  {
    title: "Digital Signatures",
    description:
      "Collect legally-binding signatures directly in your forms with our secure signature capture technology. No additional tools needed.",
    icon: FileSignature,
  },
  {
    title: "QR Code Generation",
    description:
      "Create custom QR codes for your forms instantly. Perfect for events, physical locations, or printed materials.",
    icon: QrCode,
  },
  {
    title: "Multi-Page Forms",
    description:
      "Create professional multi-step forms with progress indicators that guide users and boost completion rates by 32%.",
    icon: PieChart,
  },
  {
    title: "Folder Organization",
    description:
      "Organize your forms in customizable folders to keep everything structured and accessible for your entire team.",
    icon: FolderTree,
  },
  {
    title: "Enterprise Security",
    description:
      "Bank-grade encryption, custom data retention policies, and access controls keep your sensitive form data protected.",
    icon: ShieldCheck,
  },
  {
    title: "Team Collaboration",
    description:
      "Work together in real-time with role-based permissions, shared templates, and multi-user workspaces.",
    icon: Users,
  },
];

const testimonials = [
  "FormCraft revolutionized our patient intake system, cutting processing time by 68% and dramatically improving satisfaction scores.",
  "Using voice commands to generate our compliance forms has eliminated what used to be a full-time position. The ROI is incredible.",
  "Our international clients can now complete complex financial forms in their native language. Completion rates up 57%, errors down 83%.",
  "The digital signature feature alone is worth the subscription. We've eliminated paper forms entirely for our field service team.",
];

const detailedTestimonials: Testimonial[] = [
  {
    content:
      "In our healthcare practice, patient intake was a major bottleneck. FormCraft has completely transformed our workflow - in two weeks, we've reduced intake time by 62% and our patients love using the voice response feature when they have trouble typing. The multilingual support is essential for our diverse patient population.",
    author: "Dr. Maya Patel",
    role: "Medical Director",
    company: "Wellspring Health Partners",
    rating: 5,
  },
  {
    content:
      "As a construction company, our field teams needed a way to complete safety forms quickly onsite. The speech-to-text feature has been revolutionary - workers can complete required documentation while keeping their hands free. Insurance compliance rate up 94% and our safety manager couldn't be happier.",
    author: "David Rodriguez",
    role: "Operations Director",
    company: "Apex Construction Group",
    rating: 5,
  },
  {
    content:
      "We handle financial client onboarding for over 200 accounts monthly. FormCraft's conditional logic made our 12-page document feel like a 3-page form, with sections that appear only when relevant. Completion time dropped from 22 minutes to just 7, and error rates are down 83%.",
    author: "Emma Tanaka",
    role: "Client Services Manager",
    company: "Meridian Financial Advisors",
    rating: 5,
  },
  {
    content:
      "Our university uses FormCraft for everything from student applications to event registrations. The accessibility features are a game-changer for our diverse student body, and the instant translation tool has boosted international student engagement by 47%. Our administration finally found a form solution the entire faculty can use.",
    author: "Prof. James Washington",
    role: "Administrative Director",
    company: "Westlake University",
    rating: 5,
  },
  {
    content:
      "Running a small law firm means endless client intake forms and legal documents. I was skeptical about AI-generated forms for legal use, but the templates are surprisingly sophisticated. The digital signature feature is fully compliant with our requirements, and we've cut admin work by 15 hours weekly. Worth every penny.",
    author: "Sophia Mendez, Esq.",
    role: "Managing Partner",
    company: "Mendez & Associates Legal",
    rating: 5,
  },
];

const pricingTiers: PricingTier[] = [
  {
    name: "Basic",
    price: "$12",
    priceYearly: "$10",
    description: "Perfect for individuals and small businesses",
    features: [
      "Unlimited forms",
      "Essential form elements",
      "500 submissions/month",
      "1 user",
      "Form templates",
      "Conditional logic",
      "Custom branding",
      "Form analytics",
      "Address fields",
      "Rating fields",
      "Slider fields",
      "Rich text fields",
    ],
    buttonText: "Start Free Trial",
    buttonVariant: "outline",
    freeTrialDays: 14,
  },
  {
    name: "Pro",
    price: "$24",
    priceYearly: "$20",
    description: "Ideal for growing businesses with more needs",
    features: [
      "Unlimited forms",
      "All form elements",
      "2,500 submissions/month",
      "3 users",
      "Form templates",
      "Conditional logic",
      "Multi-page forms",
      "Character limits and validation",
      "Custom branding",
      "Form analytics",
      "Table entry fields",
    ],
    popular: true,
    buttonText: "Start Free Trial",
    freeTrialDays: 14,
  },
  {
    name: "Business",
    price: "$49",
    priceYearly: "$41",
    description: "For businesses with high volume needs",
    features: [
      "Unlimited forms",
      "All form elements",
      "10,000 submissions/month",
      "10 users",
      "Form templates",
      "Conditional logic",
      "Multi-page forms",
      "Character limits and validation",
      "Custom branding",
      "Form analytics",
      "AI form generation",
      "Priority support",
    ],
    buttonText: "Start Free Trial",
    buttonVariant: "outline",
    freeTrialDays: 14,
  },
];

export default Index;
